import { useEffect } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAppStore } from '@/stores/useAppStore';
import { useCoachingStore } from '@/stores/useCoachingStore';
import { performanceService } from '@/services/performanceService';

/**
 * Hook to automatically suggest habit adjustments based on performance
 * Analyzes adherence rates and triggers coaching interventions
 */
export function useAutoAdjust() {
  const { user } = useAuthStore();
  const { habits } = useAppStore();
  const { triggerIntervention } = useCoachingStore();

  useEffect(() => {
    if (!user || !habits || habits.length === 0) return;

    const analyzeHabits = async () => {
      const activeHabits = habits.filter(
        (h) => h.active && h.userId === user.id && h.autoAdjustEnabled
      );

      try {
        for (const habit of activeHabits) {
          const suggestions = await performanceService.analyzeForAutoAdjust(habit.id);

          if (suggestions.needsAdjustment) {
            // Trigger coaching intervention with adjustment suggestion
            await triggerIntervention('adjustment_suggestion', {
              habitId: habit.id,
              habitTitle: habit.title,
              reason: suggestions.reason,
              suggestion: suggestions.suggestion,
              date: new Date().toISOString(),
            });
          }
        }
      } catch (error) {
        console.error('Error analyzing habits for auto-adjust:', error);
      }
    };

    // Analyze weekly (every 7 days)
    analyzeHabits();
    const interval = setInterval(analyzeHabits, 7 * 24 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, habits, triggerIntervention]);
}
